// src/components/ActualizacionBanner.jsx
// Barra "Hay una versión nueva — Actualizar", SOLO PARA EL ADMIN. La app guarda una copia de sí misma
// para abrir rápido (service worker) y por eso, tras un deploy, enseñaba la versión vieja hasta abrirla
// dos veces. A los clientes no se les muestra nada (ni se les hace la revisión): se actualizan solos en
// segundo plano, como siempre, y a ellos no les cambia nada que deba preocuparles.
// Se revisa sola si el servidor ya tiene otra versión: al abrir, al volver a la app, al recuperar la
// conexión y cada 10 minutos. "Actualizar" descarga primero lo nuevo y solo si se pudo cambia la copia
// guardada y recarga; con mala señal no toca nada.
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { hayVersionNueva, bundleDe, CADA_MS } from '../services/actualizacion';

const srcActual = () => document.querySelector('script[src*="/static/js/main."]')?.src || '';

export default function ActualizacionBanner() {
  const { isAdmin } = useAuth();
  return isAdmin ? <AvisoActualizacion /> : null;
}

export function AvisoActualizacion() {
  const [hay, setHay] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [cargando, setCargando] = useState(false);
  const [ocultaHasta, setOcultaHasta] = useState(0);
  const revisando = useRef(false);

  useEffect(() => {
    const actual = srcActual();
    if (!actual) return undefined; // en desarrollo (npm start) no hay main.<hash>.js
    const revisar = async () => {
      if (revisando.current || document.visibilityState === 'hidden' || navigator.onLine === false) return;
      revisando.current = true;
      try {
        // "_ver" hace que el service worker deje pasar la petición y no sirva la copia guardada.
        const res = await fetch(`/index.html?_ver=${Date.now()}`, { cache: 'no-store' });
        if (res.ok && hayVersionNueva(await res.text(), actual)) setHay(true);
      } catch { /* sin señal: se vuelve a intentar luego */ }
      revisando.current = false;
    };
    const t0 = setTimeout(revisar, 8000);
    const iv = setInterval(revisar, CADA_MS);
    const alVolver = () => { if (document.visibilityState === 'visible') revisar(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('online', revisar);
    return () => {
      clearTimeout(t0); clearInterval(iv);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('online', revisar);
    };
  }, []);

  const actualizar = async () => {
    setCargando(true);
    setMensaje('');
    try {
      // 1) Bajar el index.html nuevo y los archivos que usa, ANTES de tocar nada.
      const res = await fetch(`/index.html?_ver=${Date.now()}`, { cache: 'no-store' });
      const html = await res.text();
      const js = bundleDe(html);
      if (!res.ok || !js) throw new Error('index');
      const css = (html.match(/\/static\/css\/main\.[A-Za-z0-9]+\.css/) || [])[0];
      const archivos = [`/static/js/${js}`, ...(css ? [css] : [])];
      const bajados = await Promise.all(archivos.map((f) => fetch(f))); // pasan por el service worker, que los guarda
      if (bajados.some((r) => !r.ok)) throw new Error('archivos');
      // 2) Recién ahora se cambia la copia guardada del index.html por la nueva.
      const claves = await caches.keys();
      await Promise.all(claves.filter((k) => k.startsWith('click-travel-shell-')).map(async (k) => {
        const c = await caches.open(k);
        const nuevo = () => new Response(html, { headers: { 'Content-Type': 'text/html; charset=utf-8' } });
        await c.put('/index.html', nuevo());
        await c.put('/', nuevo());
      }));
      window.location.reload();
    } catch {
      setCargando(false);
      setMensaje('Sin señal suficiente para actualizar. Inténtalo de nuevo en un momento.');
    }
  };

  if (!hay || Date.now() < ocultaHasta) return null;
  return (
    <div role="status" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000, background: 'var(--orange, #FF6B00)', color: '#fff',
      padding: 'calc(env(safe-area-inset-top, 0px) + 8px) 12px 8px', display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 13, fontWeight: 600, boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
    }}>
      <span style={{ flex: 1, lineHeight: 1.3 }}>{mensaje || 'Hay una versión nueva de la app.'}</span>
      <button type="button" disabled={cargando} onClick={actualizar} style={{ background: '#fff', color: 'var(--orange, #FF6B00)', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 800 }}>{cargando ? 'Bajando…' : 'Actualizar'}</button>
      <button type="button" aria-label="Ahora no" onClick={() => setOcultaHasta(Date.now() + 30 * 60 * 1000)} style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 18, lineHeight: 1, padding: '4px 6px' }}>×</button>
    </div>
  );
}
